import { Button, Form, Spinner, Table as BootstrapTable } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NewRequestTableRow } from './NewRequestTableRow';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { NewRequestContext } from '../../context/NewRequestContext';
import { BranchesContext } from '../../context/BranchesContext';
import { resourceCache, UpdaterFn } from 'use-async-resource';
import {
    editBranchPaymentInfo,
    updateMaintenanceDate,
    useGetBranchPaymentInfo,
    useGetNewRequestBranchesByClient
} from '../../utils/clients-hooks';
import { BranchPaymentInfo, MaterialBranch, NewRequestBranch } from '../../models/dashboard-models';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { getPricesOfPassports } from '../../utils/warehouses-hooks';
import { BranchDevice } from '../../models/warehouse-models';
import Select from 'react-select';
import {
    fetchBranchImage,
    fetchMapBranches,
    fetchRemindersAll,
    fetchRemindersYours,
} from '../../utils/dashboard-hooks';
import { Invoice } from '../../models/table-models';
import { InfoModal } from '../InfoModal';
import {DatePickerWrapper} from "../DatePickerWrapper";
import dateFormat from "dateformat";
import { EmailChipInput } from '../EmailChipInput';

type PassportSumPriceRequestDTO = {
    devicePassportId: number;
    oilPassportId?: number | null;
    batteryPassportId?: number | null;
};

export const NewRequestInvoices = ({ paidInvoices, unpaidInvoices }: { paidInvoices: Invoice[], unpaidInvoices:Invoice[] }) => {
    const { selectedBranch, branchPaymentInfo, getBranchPaymentInfo, devices, materials, resourcesLoadFailed } =
        useContext(NewRequestContext);
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });

    // Never render the form without this branch's data: reset(undefined) keeps the previous
    // branch's values and saving them would overwrite the new branch's payment settings.
    if (!branchPaymentInfo) {
        return (
            <div className='w-100 p-2 mt-4' style={{ minWidth: 450 }}>
                <BootstrapTable className='w-100'>
                    <thead>
                        <tr>
                            <th className='text-center border-top fw-bold'>{t('payment information')}</th>
                        </tr>
                    </thead>
                </BootstrapTable>
                {!resourcesLoadFailed && (
                    <div className='d-flex justify-content-center py-5'>
                        <Spinner animation='border' role='status' />
                    </div>
                )}
            </div>
        );
    }

    return (
        <BranchPaymentData
            key={selectedBranch.id}
            {...{
                selectedBranch,
                branchPaymentInfo,
                getBranchPaymentInfo,
                devices: devices?.items,
                materials,
                deviceInstallation: false,
                viewOnly: false,
                paidInvoices,
                unpaidInvoices
            }}
        />
    );
};

interface BranchPaymentDataProps {
    selectedBranch: NewRequestBranch;
    branchPaymentInfo?: BranchPaymentInfo;
    getBranchPaymentInfo: UpdaterFn<[id: number]>;
    devices?: BranchDevice[] | undefined;
    materials: MaterialBranch[] | undefined;
    deviceInstallation: boolean;
    viewOnly?: boolean;
    paidInvoices?: Invoice[];
    unpaidInvoices?: Invoice[];
}

export const BranchPaymentData = (props: BranchPaymentDataProps) => {
    const { getBranches, selectedClient, updateBranchAfterSave } = useContext(NewRequestContext);
    const { updateBranchLocal } = useContext(BranchesContext);
    return <MemoizedBranchPaymentData {...props} {...{ getBranches, selectedClient, updateBranchAfterSave, updateBranchLocal }} />;
};

type PaymentFormProps = BranchPaymentDataProps &
    Pick<React.ContextType<typeof NewRequestContext>, 'getBranches' | 'selectedClient' | 'updateBranchAfterSave'> &
    Pick<React.ContextType<typeof BranchesContext>, 'updateBranchLocal'>;

const MemoizedBranchPaymentData = React.memo(({
    selectedBranch, branchPaymentInfo, getBranchPaymentInfo, devices, materials,
    deviceInstallation, viewOnly, paidInvoices, unpaidInvoices,
    getBranches, selectedClient, updateBranchAfterSave, updateBranchLocal,
}: PaymentFormProps) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { t: commonT } = useTranslation('translation', { keyPrefix: 'common' });
    const today = dateFormat(new Date(), 'yyyy-mm-dd');
    const { control, handleSubmit, reset, watch, setValue, getValues } = useForm({
        defaultValues: useMemo(() => {
            return branchPaymentInfo;
        }, [branchPaymentInfo]),
    });
    const [paymentPeriodSwitch, setPaymentPeriodSwitch] = useState(branchPaymentInfo?.paymentPeriod ?? 1);

    const [hasChangedPayDay, setHasChangedPayDay] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [freeTextReason, setFreeTextReason] = useState('');
    const [selectedDate, setSelectedDate] = useState<string>(selectedBranch.nextMaintenanceDate ?? "");
    const feeRequest = useRef(0);
    useLayoutEffect(() => {
        feeRequest.current += 1;
        return () => { feeRequest.current += 1; };
    }, [selectedBranch.id, branchPaymentInfo, devices, deviceInstallation]);

    useEffect(() => {
        setSelectedDate(selectedBranch.nextMaintenanceDate ?? "")
    }, [selectedBranch]);

    useEffect(() => {
        // New //
        localStorage.setItem('paymentInfo', JSON.stringify(branchPaymentInfo));
        // New END//
        reset(branchPaymentInfo);
        setValue('feePeriod', branchPaymentInfo!=undefined ? branchPaymentInfo.fee*branchPaymentInfo?.invoicingMonths:0);
    }, [branchPaymentInfo]);

    useEffect(() => {
        // Wait for both resources; undefined means not loaded, not a zero fee.
        if (!branchPaymentInfo || !devices || branchPaymentInfo.fee) return;
        calculateSummaryOfPassportFees();
    }, [selectedBranch.id, branchPaymentInfo, devices, deviceInstallation]);

    useEffect(() => {
        const addition: number = watch('addition') || 0;
        const discount = parseFloat(addition.toString().replace(',', '.'));

        const months = watch('invoicingMonths')||1;
        const fee = parseFloat(watch('fee')?.toString().replace(',', '.') || '0');

        const total: number = parseFloat(fee.toString());
        const discountedFee = (discount / 100) * total;
        const fullFeeForPrepaidMonths = (total + discountedFee) * months;
        setValue('total', +fullFeeForPrepaidMonths.toFixed(2));
        setValue('feePeriod', +(total*months).toFixed(2));
    }, [watch('fee'), watch('discount'), watch('addition'), watch('invoicingMonths'), devices]);

    const handleOnChangePaymentDay = (onChange: (...event: any[]) => void, event: any) => {
        onChange(event);
        const paymentDate = new Date(watch("paymentDate"));
        const nextMaintenanceDate = new Date(selectedBranch.nextMaintenanceDate);

        const newPaymentDate = new Date(paymentDate.getFullYear(), paymentDate.getMonth(), event.target.value);
        const newNextMaintenanceDate = new Date(nextMaintenanceDate.getFullYear(), nextMaintenanceDate.getMonth(), event.target.value);

        setValue('paymentDate', dateFormat(newPaymentDate, 'yyyy-mm-dd'));
        setSelectedDate(() => dateFormat(newNextMaintenanceDate, 'yyyy-mm-dd'));
        setHasChangedPayDay(true);
    };

    // „Следваща дата на поддръжка" не се пипа: настройката мести само задачата, която се генерира от нея
    // (и вече създадената задача - при запис, от backend-а).
    const handleOnChangeServiceDayOffset = (onChange: (...event: any[]) => void, offset: number) => {
        onChange(offset);
        setHasChangedPayDay(true);
    };

    // Отчетната дата, най-близка до date: в същия месец, в предишния или в следващия. Месец без отчетния
    // ден минава на 1-во число на следващия - така го смятат timer-service и work-service.
    const nearestReportDate = (date: Date, reportDay: number) => {
        const candidates = [-1, 0, 1].map((months) => {
            const year = date.getFullYear();
            const month = date.getMonth() + months;
            return reportDay > new Date(year, month + 1, 0).getDate()
                ? new Date(year, month + 1, 1)
                : new Date(year, month, reportDay);
        });
        return candidates.reduce((nearest, candidate) =>
            Math.abs(candidate.getTime() - date.getTime()) < Math.abs(nearest.getTime() - date.getTime()) ? candidate : nearest);
    };

    // Денят на задачата е до 3 дни преди или след отчетната дата - колкото е гратисът около нея. Датите
    // тръгват от отчетната дата на текущата „Следваща дата на поддръжка", дори тя да е местена на ръка.
    const serviceDayOptions = () => {
        const planned = new Date(selectedBranch.nextMaintenanceDate ?? watch('paymentDate'));
        const reportDay = Number(watch('paymentDay')) || 1;
        const base = nearestReportDate(isNaN(planned.getTime()) ? new Date() : planned, reportDay);
        return [-3, -2, -1, 0, 1, 2, 3].map((offset) => {
            const date = dateFormat(new Date(base.getFullYear(), base.getMonth(), base.getDate() + offset), 'dd/mm/yyyy');
            const label = offset === 0
                ? t('serviceDayReportDay', { date })
                : t(offset < 0 ? 'serviceDayBefore' : 'serviceDayAfter', { date, count: Math.abs(offset) });
            return { value: offset, label };
        });
    };

    const calculateSummaryOfPassportFees = async () => {
        if (!branchPaymentInfo || !devices) return;
        const request = ++feeRequest.current;
        const initialFee = getValues('fee');
        const payload: PassportSumPriceRequestDTO[] = [];

        devices?.forEach((value) => {
            if (value.active == null || value.active) {
                payload.push({
                    devicePassportId: value.passportId,
                    oilPassportId: value.oilId ?? null,
                    batteryPassportId: value.batteryId ?? null,
                });
            }
        });

        // ако имате и материали, може:
        // 1) да ги пращате към стария ендпойнт, или
        // 2) да разширите бекенда да приема и "extraPassportIds"

        let passportsFee: number;
        try {
            passportsFee = await getPricesOfPassports(payload);
        } catch (error) {
            console.error('Failed to calculate passport fees', error);
            return;
        }
        if (request !== feeRequest.current || !Object.is(initialFee, getValues('fee'))) return;

        let totalFee = passportsFee;
        if (deviceInstallation && branchPaymentInfo !== undefined) {
            totalFee = totalFee + branchPaymentInfo.fee * 100;
        }
        setValue('fee', totalFee / 100);
    };

    const editSubmitHandler = async (formValue: BranchPaymentInfo) => {
        if (hasChangedPayDay) {
            setHasChangedPayDay(false);
            setShowModal(true);
            return;
        }
        if (selectedBranch.id) {
            const addition: number = watch('addition') || 0;
            setValue('addition', parseFloat(addition.toString().replace(',', '.')));
            resourceCache(useGetBranchPaymentInfo).clear();
            const response = await editBranchPaymentInfo({...formValue,addition: parseFloat(addition.toString().replace(',', '.'))}, selectedBranch.id, freeTextReason);
            if (response !== undefined) {
                toast(<CustomToastContent content={[t('invoiceSuccess')]} />, {
                    position: 'top-right',
                    autoClose: 5000,
                    hideProgressBar: true,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    type: 'success',
                });
            }
            if(selectedDate!=selectedBranch.nextMaintenanceDate){
                await updateMaintenanceDate(selectedBranch.id, selectedDate);

                // OPTIMISTIC UPDATE: Update local state instead of clearing cache
                const mapBranch = {
                    id: selectedBranch.id || 0,
                    name: selectedBranch.name,
                    state: selectedBranch.state || 'GREEN',
                    phone: selectedBranch.phone || '',
                    latitude: selectedBranch.location.latitude,
                    longitude: selectedBranch.location.longitude,
                    city: selectedBranch.location.city,
                    assignedDistributor: (typeof selectedBranch.assignedDistributor === 'object'
                        ? selectedBranch.assignedDistributor.id
                        : selectedBranch.assignedDistributor) || 0,
                    branchAddress: selectedBranch.location.address,
                    companyName: selectedClient.companyName,
                    assignedTask: false,
                    testPeriod: false,
                    batteryPercent: 0,
                    maintenanceDays: 0,
                    nextMaintenanceDate: selectedDate, // Updated maintenance date
                    oilPercent: 0,
                    batteryState: 'GREEN',
                    oilState: 'GREEN',
                    maintenanceState: 'GREEN',
                    groupName: selectedBranch.groupName,
                    price: 0,
                    paymentDate: '',
                };

                updateBranchLocal(mapBranch);

                resourceCache(useGetNewRequestBranchesByClient).clear();
                // REMOVED: resourceCache(fetchMapBranches).clear(); (was called twice!)
                // REMOVED: getNewBranches({ filter: {} });
                resourceCache(fetchBranchImage).clear();
                selectedClient && selectedClient.id && getBranches(selectedClient.id);
                const updatedBranch = {...selectedBranch, nextMaintenanceDate: selectedDate};
                updateBranchAfterSave(updatedBranch);
            }
            resourceCache(fetchRemindersAll).clear();
            resourceCache(fetchRemindersYours).clear();
            resourceCache(useGetNewRequestBranchesByClient).clear();
            await getBranchPaymentInfo(selectedBranch.id);
            setHasChangedPayDay(false);
        }
    };

    const discountValues = ['0', '5', '10', '15', '20'];
    const paymentMethods = ['CASH', 'BANK'];
    const paymentTypes = ['INVOICE', 'RECEIPT'];

    return (
        <div className='w-100 d-flex flex-column align-items-center '>
            <Form id='paymentInfo' className='w-100 p-2 mt-4 ' onSubmit={handleSubmit(editSubmitHandler)}>
                <InfoModal
                    show={showModal}
                    text={''}
                    buttonText={'save'}
                    onHide={(isSave: boolean) => {
                        if (isSave) {
                            const formValues = { ...control._formValues };
                            editSubmitHandler(formValues as BranchPaymentInfo).then();
                        } else {
                            setHasChangedPayDay(true);
                        }
                        setFreeTextReason('');
                        return setShowModal(false);
                    }}
                >
                    <h5 className='w-100 mb-0 py-3'>{t('reason')}</h5>

                    <Form.Control
                        placeholder={commonT('freetext')}
                        className={'freetext'}
                        name={'freetext'}
                        onChange={(e) => setFreeTextReason(e.currentTarget.value)}
                        value={freeTextReason ?? ''}
                    />
                </InfoModal>
                <BootstrapTable className='w-100 position-relative'>
                    <thead>
                        <tr className='position-relative'>
                            <th colSpan={2} className='text-center border-top fw-bold'>
                                {t('payment information')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className='d-flex flex-column'>
                        <NewRequestTableRow
                            id='paymentDay'
                            label={t('date')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='paymentDay'
                                {...{ control }}
                                render={({ field: { value, name, onChange } }) => (
                                    <>
                                        <Form.Control
                                            type='number'
                                            disabled={viewOnly}
                                            min='1'
                                            max='31'
                                            className='w-30 text-center p-0'
                                            value={value ?? ''}
                                            name={name}
                                            onChange={(event) => handleOnChangePaymentDay(onChange, event)}
                                        />
                                        <span className='ps-2'>{t('dayOfMonth')}</span>
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='paymentDate'
                            label={t('paymentDate')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <div className={"w-50"}>
                                <Controller
                                    name='paymentDate'
                                    {...{control}}
                                    render={({field: {value}}) => (
                                        <DatePickerWrapper
                                            minDate={today}
                                            selected={value ?? ''}
                                            onChange={(value) => {
                                                setValue("paymentDate", value)
                                            }}
                                        />
                                    )}
                                />
                            </div>
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='nextMaintenanceDate'
                            label={t('nextMaintenanceDate')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            {selectedBranch.nextMaintenanceDate == null ?
                                <div>{t("NoDate")}</div> :
                                <div className={"w-50"}>
                                    <DatePickerWrapper
                                        minDate={today}
                                        selected={selectedDate ?? ''}
                                        onChange={(value) => {setSelectedDate(value); setHasChangedPayDay(true)}}
                                    />
                                </div>
                            }
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='serviceDayOffset'
                            label={t('serviceDay')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='serviceDayOffset'
                                control={control}
                                defaultValue={0}
                                render={({ field }) => {
                                    const selectOptions = serviceDayOptions();
                                    return (
                                        <Select
                                            className={'w-100 p-0'}
                                            options={selectOptions}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                // Етикетът е дата с бележка - да не се реже и да не скрива стрелката.
                                                control: (base) => ({ ...base, minWidth: 230 }),
                                                singleValue: (base) => ({ ...base, overflow: 'visible' }),
                                                menu: (base) => ({ ...base, minWidth: 230 }),
                                                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                            }}
                                            isDisabled={viewOnly}
                                            value={selectOptions.find((option) => option.value === (field.value ?? 0))}
                                            onChange={(option) =>
                                                handleOnChangeServiceDayOffset(field.onChange, option ? option.value : 0)
                                            }
                                        />
                                    );
                                }}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='paymentPeriod'
                            label={t('paymentPeriod')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='paymentPeriod'
                                control={control}
                                defaultValue={1}
                                render={({ field }) => {
                                    const selectOptions = [
                                        { value: 1, label: '1' },
                                        { value: 2, label: '2' },
                                        { value: 3, label: '3' },
                                        { value: 4, label: '4' },
                                    ];
                                    return (
                                        <>
                                            <Select
                                                className={'w-30 text-center p-0'}
                                                options={selectOptions}
                                                isDisabled={viewOnly}
                                                value={selectOptions.find((option) => option.value === field.value)}
                                                onChange={(option) => {
                                                    field.onChange(
                                                        option ? option.value : branchPaymentInfo?.paymentPeriod ?? 1
                                                    );
                                                    setValue(
                                                        'invoicingMonths',
                                                        option
                                                            ? option.value
                                                            : branchPaymentInfo?.invoicingMonths ?? 1
                                                    );
                                                    setPaymentPeriodSwitch(
                                                        option ? option.value : branchPaymentInfo?.paymentPeriod ?? 1
                                                    );
                                                }}
                                            />
                                            <span className='ps-2'>{t('months')}</span>
                                        </>
                                    );
                                }}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='paymentType'
                            label={t('paymentType')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='paymentType'
                                {...{ control }}
                                render={({ field }) => (
                                    <>
                                        {paymentTypes.map((value, index) => (
                                            <Form.Check
                                                key={index}
                                                className='p-0 d-flex justify-content-center align-items-center  px-1'
                                            >
                                                <Form.Check.Input
                                                    id={`paymentType${index}`}
                                                    disabled={viewOnly}
                                                    type='radio'
                                                    className='m-0 '
                                                    checked={value === field.value}
                                                    {...field}
                                                    {...{ value }}
                                                />
                                                <Form.Check.Label htmlFor={`paymentType${index}`} className='px-1'>
                                                    {t(value)}
                                                </Form.Check.Label>
                                            </Form.Check>
                                        ))}
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='paymentMethod'
                            label={t('paymentMethod')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='paymentMethod'
                                {...{ control }}
                                render={({ field }) => (
                                    <>
                                        {paymentMethods.map((value, index) => (
                                            <Form.Check
                                                key={index}
                                                className='p-0 d-flex justify-content-center align-items-center  px-1'
                                            >
                                                <Form.Check.Input
                                                    id={`paymentMethod${index}`}
                                                    type='radio'
                                                    disabled={viewOnly}
                                                    className='m-0 '
                                                    checked={value === field.value}
                                                    {...field}
                                                    {...{ value }}
                                                />
                                                <Form.Check.Label htmlFor={`paymentMethod${index}`} className='px-1'>
                                                    {t(value)}
                                                </Form.Check.Label>
                                            </Form.Check>
                                        ))}
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='email'
                            label={t('emailForInvoices')}
                            inputContainerClass='w-100'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='email'
                                {...{ control }}
                                render={({ field }) => (
                                    <EmailChipInput
                                        id='branch-email'
                                        name={field.name}
                                        value={field.value ?? ''}
                                        placeholder={t('emailsPlaceholder') || 'напр. office@client.com, acc@client.com'}
                                        onChange={field.onChange}
                                        onBlur={field.onBlur}
                                        disabled={viewOnly}
                                    />
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='testPeriod'
                            label={t('testPeriod')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='testPeriod'
                                {...{ control }}
                                render={({ field }) => (
                                   <>

                                    <Form.Check
                                        disabled={viewOnly || (paidInvoices && paidInvoices?.length > 0) ||
                                            (unpaidInvoices && unpaidInvoices?.length > 0) || branchPaymentInfo?.testPeriod == true}
                                        checked={field.value ?? false}
                                        onChange={field.onChange}
                                    />
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='fee'
                            label={t('feeForMonth')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='fee'
                                {...{ control }}
                                render={({ field }) => (
                                    <>

                                        <Form.Control
                                            type='currency'
                                            disabled={viewOnly}
                                            min='0'
                                            className='w-30 text-center p-0'
                                            value={field.value ?? ''}
                                            onChange={(event) => {
                                                feeRequest.current += 1;
                                                field.onChange(event);
                                            }}
                                            onBlur={field.onBlur}
                                            ref={field.ref}
                                            name={field.name}
                                        />
                                        <span className='ps-2'>{t('feeCurrency')}</span>
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='invoicingMonths'
                            label={t('prepayment')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='invoicingMonths'
                                control={control}
                                defaultValue={1}
                                render={({ field }) => {
                                    let numbers = Array.from({ length: 12 }, (_, i) => i + 1);
                                    if (paymentPeriodSwitch >= 2 && paymentPeriodSwitch <= 4)
                                        numbers = numbers.filter((number) => number % paymentPeriodSwitch === 0);
                                    const selectOptions = numbers.map((value) => {
                                        return { label: value.toString(), value: value ?? '' };
                                    });
                                    return (
                                        <>
                                            <Select
                                                className={'w-30 text-center p-0'}
                                                options={selectOptions}
                                                isDisabled={viewOnly}
                                                value={selectOptions.find((option) => option.value === field.value)}
                                                onChange={(option) =>
                                                    option
                                                        ? field.onChange(option.value)
                                                        : field.onChange(branchPaymentInfo?.invoicingMonths ?? 1)
                                                }
                                            />
                                            <span className='ps-2'>{t('months')}</span>
                                        </>
                                    );
                                }}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='feePeriod'
                            label={t('feePeriod')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='feePeriod'
                                {...{ control }}
                                render={({ field }) => (
                                    <>

                                        <Form.Control
                                            type='currency'
                                            disabled={true}
                                            min='0'
                                            className='w-30 text-center p-0'
                                            value={field.value ?? ''}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            ref={field.ref}
                                            name={field.name}
                                        />
                                        <span className='ps-2'>{t('feeCurrency')}</span>
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='addition'
                            label={t("discount")+"(- %) "+t('addition')+"(+%)"}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='addition'
                                {...{ control }}
                                render={({ field }) => (
                                    <>

                                        <Form.Control
                                            type='currency'
                                            disabled={viewOnly}
                                            className='w-30 text-center p-0'
                                            value={field.value ?? ''}
                                            onChange={field.onChange}
                                            onBlur={field.onBlur}
                                            ref={field.ref}
                                            name={field.name}
                                        />
                                        <span className='ps-2'>%</span>
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            id='total'
                            label={t('total')}
                            inputContainerClass='w-50'
                            labelContainerClass='w-50'
                        >
                            <Controller
                                name='total'
                                {...{ control }}
                                render={({ field }) => (
                                    <>
                                        <Form.Control
                                            type='currency'
                                            disabled={true}
                                            className='w-30 text-center p-0'
                                            value={field.value ? field.value.toString() : ''}
                                            onChange={field.onChange}
                                            name={field.name}
                                            onBlur={field.onBlur}
                                            ref={field.ref}
                                        />
                                        <span className='ps-2'>{branchPaymentInfo?.currency}</span>
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                    </tbody>
                </BootstrapTable>
                <div className='d-flex justify-content-center flex-nowrap'>
                    <Button disabled={!branchPaymentInfo || !devices} onClick={calculateSummaryOfPassportFees} className='w-40 mx-1  mt-2'>
                        {t('recalculate')}
                    </Button>
                    {!viewOnly && (
                        <Button form='paymentInfo' type='submit' className='w-25 mx-1  mt-2 btn-success'>
                            {t('save')}
                        </Button>
                    )}
                </div>
            </Form>
        </div>
    );
});
